import * as React from 'react';
import type { FC } from 'react';

import type { BffResponse, GMCBundlesListResponse } from '@visualizer/bff';

import { useAssetBundleSorting } from './use-asset-bundle-sorting';

import { type AssetBundleRow } from './utils';

import { DataTable, type DataTableHeader, type DataTableRow } from './data-table';

type AssetBundlesResponse = BffResponse<GMCBundlesListResponse>;
type AssetBundleEntry = AssetBundlesResponse['payload']['assetBundles'][number];
interface AssetBundlesTableProps {
	data?: AssetBundlesResponse;
	isLoading: boolean;
	error?: Error;
}

const VEHICLE_CONFIG_VARIANTS_PARAMETERS = '@gmc/vehicle-configuration-variants-generator.parameters';

type VehicleConfigurationVariantsParametersAttribute = Extract<
	AssetBundleEntry['latestVersion']['attributes'][number],
	{ type: typeof VEHICLE_CONFIG_VARIANTS_PARAMETERS }
>;

const isVehicleConfigurationVariantsParametersAttribute = (
	attribute: AssetBundleEntry['latestVersion']['attributes'][number]
): attribute is VehicleConfigurationVariantsParametersAttribute =>
	attribute.type === VEHICLE_CONFIG_VARIANTS_PARAMETERS;

function toTableRow(bundle: AssetBundleEntry): AssetBundleRow {
	const latestVersion = bundle.latestVersion;
	const attributes = latestVersion?.attributes ?? [];
	const { title, brand, modelYear } = attributes.find(isVehicleConfigurationVariantsParametersAttribute) ?? {};

	return {
		id: bundle.id ?? '—',
		title: title ?? '—',
		brand: brand ?? '—',
		modelYear: modelYear || '—',
		modelYearValue: modelYear ?? null
	};
}

export const AssetBundlesTable: FC<AssetBundlesTableProps> = ({ data, isLoading, error }) => {
	const bundleRows = data?.payload?.assetBundles ? data.payload.assetBundles.map(toTableRow) : [];

	const { sortedRows: sortedBundleRows, sortIndicator, sortAriaLabel, toggleSort } = useAssetBundleSorting(bundleRows);

	const brandSortIndicator = sortIndicator('brand');
	const modelYearSortIndicator = sortIndicator('modelYear');

	const headers: DataTableHeader[] = [
		{ key: 'position', content: '#' },
		{ key: 'title', content: 'Title' },
		{ key: 'bundle-id', content: 'Bundle ID' },
		{
			key: 'brand',
			content: (
				<button type="button" onClick={() => toggleSort('brand')} aria-label={sortAriaLabel('Brand', 'brand')}>
					Brand
					{brandSortIndicator ? <span aria-hidden="true">{brandSortIndicator}</span> : null}
				</button>
			)
		},
		{
			key: 'model-year',
			content: (
				<button
					type="button"
					onClick={() => toggleSort('modelYear')}
					aria-label={sortAriaLabel('Model Year', 'modelYear')}
				>
					Model Year
					{modelYearSortIndicator ? <span aria-hidden="true">{modelYearSortIndicator}</span> : null}
				</button>
			)
		}
	];

	if (isLoading) {
		const rows: DataTableRow[] = [
			{
				key: 'loading',
				cells: [{ key: 'loading', content: 'Loading asset bundles…', colSpan: headers.length }]
			}
		];

		return <DataTable headers={headers} rows={rows} fallback="No asset bundles available." />;
	}

	if (error) {
		const rows: DataTableRow[] = [
			{
				key: 'error',
				cells: [
					{
						key: 'error',
						content: `Failed to load asset bundles: ${error.message}`,
						colSpan: headers.length
					}
				]
			}
		];

		return <DataTable headers={headers} rows={rows} fallback="No asset bundles available." />;
	}

	const rows: DataTableRow[] = sortedBundleRows.map((row, index) => ({
		key: `${row.id}-${index}`,
		cells: [
			{ key: 'position', content: index + 1 },
			{ key: 'title', content: row.title },
			{ key: 'bundle-id', content: row.id },
			{ key: 'brand', content: row.brand },
			{ key: 'model-year', content: row.modelYear }
		]
	}));

	return <DataTable headers={headers} rows={rows} fallback="No asset bundles available." />;
};
